<template>
    <div class="map-container">
  
      <!-- 🔎 Поле ввода адреса -->
      <div class="search-block" :class="{ disabled: !isEnabled }">
        <input
            v-model="searchQuery"
            @keyup.enter="isEnabled ? searchAddress() : null"
            class="search-input"
            type="text"
            :disabled="!isEnabled"
            placeholder="Введите адрес..."
          />

          <button 
            class="search-btn" 
            @click="isEnabled ? searchAddress() : null"
            :disabled="!isEnabled"
          >
            Найти
          </button>
      </div>
  
      <!-- 🗺 КАРТА -->
      <div ref="mapRef" class="choose-map" :class="{ disabled: !isEnabled }"></div>
  
      <!-- 💾 Сохранить -->
      <button
        class="save-btn"
        :disabled="!hasChanges"
        @click="saveLocation"
        >
        Сохранить локацию
    </button>
    </div>
  </template>
  
  <script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Loader } from "@googlemaps/js-api-loader";

const props = defineProps({
  lat: { type: Number, default: 55.751244 },
  lng: { type: Number, default: 37.618423 },
  isEnabled: { type: Boolean, default: true },
  companyId: { type: String, required: true }
});

const emit = defineEmits(["locationSelected", "save"]);

const mapRef = ref(null);
let map = null;
let marker = null;
let geocoder = null;
let themeObserver = null;

const initialized = ref(false);
const hasChanges = ref(false);
const searchQuery = ref("");
const currentAddress = ref("");

const API_KEY = "AIzaSyBSEN-wo__FMd5MeQUIkQYGFPuqZeHgVNY";

/* --------------------- СТИЛИ КАРТ --------------------- */

const lightMapStyle = [
  { "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }] },
  { "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
  { "elementType": "labels.text.fill", "stylers": [{ "color": "#616161" }] },
  { "elementType": "labels.text.stroke", "stylers": [{ "color": "#f5f5f5" }] },
  { "featureType": "administrative.land_parcel", "elementType": "labels.text.fill", "stylers": [{ "color": "#bdbdbd" }] },
  { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#eeeeee" }] },
  { "featureType": "poi", "elementType": "labels.text.fill", "stylers": [{ "color": "#757575" }] },
  { "featureType": "poi.park", "elementType": "geometry", "stylers": [{ "color": "#e5e5e5" }] },
  { "featureType": "poi.park", "elementType": "labels.text.fill", "stylers": [{ "color": "#9e9e9e" }] },
  { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#ffffff" }] },
  { "featureType": "road.arterial", "elementType": "labels.text.fill", "stylers": [{ "color": "#757575" }] },
  { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#dadada" }] },
  { "featureType": "road.highway", "elementType": "labels.text.fill", "stylers": [{ "color": "#616161" }] },
  { "featureType": "road.local", "elementType": "labels.text.fill", "stylers": [{ "color": "#9e9e9e" }] },
  { "featureType": "transit.line", "elementType": "geometry", "stylers": [{ "color": "#e5e5e5" }] },
  { "featureType": "transit.station", "elementType": "geometry", "stylers": [{ "color": "#eeeeee" }] },
  { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#c9c9c9" }] },
  { "featureType": "water", "elementType": "labels.text.fill", "stylers": [{ "color": "#9e9e9e" }] }
];

const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#2e2e2e" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#181818" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#383838" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#484848" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#505050" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f1f1f" }] },
  { featureType: "road.highway.controlled_access", elementType: "geometry", stylers: [{ color: "#4e4e4e" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f2f2f" }] },
  { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#3a3a3a" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] }
];

const getTheme = () =>
  document.documentElement.getAttribute("data-theme") || "light";

/* --------------------- ПЕРЕСТРОЙКА КАРТЫ --------------------- */

function rebuildMap() {
  if (!map || !marker) return;

  const center = map.getCenter();
  const zoom = map.getZoom();

  marker.setMap(null);

  map = new google.maps.Map(mapRef.value, {
    center,
    zoom,
    disableDefaultUI: true,
    styles: getTheme() === "dark" ? darkMapStyle : lightMapStyle,
  });

  marker = new google.maps.Marker({
    map,
    position: center,
    draggable: true,
  });

  if (props.isEnabled) {
    map.addListener("click", (e) => updateMarker(e.latLng.lat(), e.latLng.lng()));
    marker.addListener("dragend", (e) => updateMarker(e.latLng.lat(), e.latLng.lng()));
  } 
}

/* --------------------- ИНИЦИАЛИЗАЦИЯ --------------------- */

async function initMap() {
  const loader = new Loader({
    apiKey: API_KEY,
    version: "weekly",
  });

  await loader.load();

  geocoder = new google.maps.Geocoder();

  const center = { lat: props.lat, lng: props.lng };

  map = new google.maps.Map(mapRef.value, {
    center,
    zoom: 15,
    disableDefaultUI: true,
    styles: getTheme() === "dark" ? darkMapStyle : lightMapStyle,
  });

  marker = new google.maps.Marker({
    map,
    position: center,
    draggable: true,
  });

  map.addListener("click", (e) => updateMarker(e.latLng.lat(), e.latLng.lng()));
  marker.addListener("dragend", (e) => updateMarker(e.latLng.lat(), e.latLng.lng()));

  updateMarker(center.lat, center.lng);

  initThemeWatcher();

  // 🔥 ВАЖНО: теперь карта считается загруженной
  initialized.value = true;  
}

/* --------------------- THEME WATCHER --------------------- */

function initThemeWatcher() {
  themeObserver = new MutationObserver(() => {
    rebuildMap();
  });

  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
}

/* --------------------- ЛОГИКА --------------------- */

function updateMarker(lat, lng) {
  if (!props.isEnabled) return;

  const pos = { lat, lng };

  marker.setPosition(pos);
  map.panTo(pos);

  if (initialized.value) {
    hasChanges.value = true;
  }

  geocoder.geocode(
    { location: pos },
    (results, status) => {
      if (status === "OK" && results[0]) {
        currentAddress.value =
          results[0].formatted_address;
      }
    }
  );
}

function searchAddress() {
  if (!searchQuery.value.trim()) return;

  geocoder.geocode({ address: searchQuery.value }, (results, status) => {
    if (status === "OK" && results[0]) {
      const loc = results[0].geometry.location;
      updateMarker(loc.lat(), loc.lng());
    }
  });
}

function saveLocation() {
  if (!currentAddress.value || !marker) {
    return;
  }

  const position = marker.getPosition();

  if (!position) {
    return;
  }

  emit("locationSelected", {
    lat: position.lat(),
    lng: position.lng(),
    address: currentAddress.value
  });

  hasChanges.value = false;
}

onBeforeUnmount(() => {
  themeObserver?.disconnect();
  marker?.setMap?.(null);
  marker = null;
  map = null;
});

onMounted(() => initMap());
</script>
  
  <style scoped>

  .map-container {
    position: relative;
    width: 740px;
  }
  
.search-block {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  margin-top: 10px;
}

.search-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #ccc;
  border-radius: 12px;
  font-size: 14px;
  margin-left: 10px;
  margin-right: 0px;
  max-width: 330px;

  background: #ffffff;
  color: #000000;

  transition: 0.2s ease;
}

.search-input:focus {
  border-color: #2173e5;
  outline: none;
  box-shadow: 0 0 0 3px rgba(33, 115, 229, 0.25);
}

[data-theme="dark"] .search-input {
  background: #2a2a2a;
  border-color: #acacac;
  color: #f1f1f1;
}

[data-theme="dark"] .search-input::placeholder {
  color: #888;
}

[data-theme="dark"] .search-input:focus {
  border-color: #2173e5;
  box-shadow: 0 0 0 3px rgba(33, 115, 229, 0.35);
}
  
  .search-btn {
    background: #2173e5;
    border: none;
    color: white;
    padding: 12px 20px;
    border-radius: 12px;
    cursor: pointer;
  }
  
  .choose-map {
    width: 740px;
    height: 300px;
    overflow: hidden;
  }
  
  .address-panel {
    margin-top: 12px;
    padding: 12px 16px;
    background: #2173e5;
    color: white;
    border-radius: 10px;
    font-size: 14px;
    width: fit-content;
  }
  
  .save-btn {
    margin-top: 14px;
    width: 200px;
    padding: 14px;
    border-radius: 12px;
    border: none;
    background: #2173e5;
    color: white;
    cursor: pointer;
    font-size: 12px;
    margin-left: 10px;
    margin-bottom: 30px;
    margin-top: 20px;
  }
  
  .save-btn:disabled {
    opacity: 0.5;
  }

.disabled {
  opacity: 0.5;
  pointer-events: none;
}

  </style>